import { Stack, Dialog, Button, Select, MenuItem, FormGroup, FormControlLabel, Checkbox, FormControl, InputLabel, FormHelperText, CircularProgress, TextField, Box } from "@mui/material";
import { User } from "../../../redux/user";
import { AutoRenewalItem } from "../../../types";
import React, { useMemo, useState } from "react";
import axios from "axios";
import constants from "../../../constants";
import { StyledDialogContent, StyledDialogFooter, StyledDialogTitle } from "../../../components/Dialog.styled";
import { addError } from '../../../redux/alerts';
import AuthService from "../../../services/authorization";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { omit } from "remeda";
import { StyledTypography } from "../../../components/Typography.styled";
import SurveyModal from "./SurveryModal";
import { itemImages } from "../../../helpers/image";
import noImage from '../../../images/no_image.png';
import CloseModalButton from '../../../components/CloseModalButton'
import utc from 'dayjs/plugin/utc'
import { useAppDispatch } from "../../../redux/store"

dayjs.extend(utc)

type SubscriptionModalProps = {
  open: boolean;
  handleClose: (success?: boolean, unEnroll?: boolean) => void;
  subscriptionItem: AutoRenewalItem | null;
  enrolled: boolean;
  customerNumber: string;
  currentUser: User | null;
}

interface FormData {
  quantity: string;
  frequency: string;
  startDate: Dayjs | null;
  hold: boolean;
}

const months = [...Array.from({ length: 12 }, (_, i) => i + 1), 36, 48, 60];
const quantities = Array.from({ length: 20 }, (_, i) => i + 1);
const initialFormDataState: FormData = {
  quantity: '',
  frequency: '',
  startDate: null,
  hold: false
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  open,
  handleClose,
  subscriptionItem,
  enrolled,
  customerNumber,
  currentUser
}) => {
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState(initialFormDataState);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<any>({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSurvery, setShowSurvery] = useState(false);
  const [datePickerIsOpen, setDatePickerIsOpen] = useState(false);
  const minStartDate = dayjs().add(1, 'month').set('date', 1).utc().startOf('day');
  const handleOpenDatePicker = () => setDatePickerIsOpen(true);

  const handleEditChange = (field: string, value: string | Dayjs | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field] && value !== '') {
      let errors = formErrors;
      delete errors[field];
      setFormErrors(errors);
    }
  };

  const validateForm = () => {
    let errors = {};
    (Object.keys(omit(formData, ['hold'])) as (keyof FormData)[]).forEach((field) => {
      const value = formData[field];
      if (value === '' || !value) {
        errors = { ...errors, [field]: true };
      }
    });

    return errors;
  }

  const logActivity = async (values: any) => {
    try {
      const authService = new AuthService();
      const adminId = authService.getAdminId();
      const userId = currentUser?.login_user_id;

      const response = await axios.get(constants.IP_LOCATOR);
      const ipAddress = response.data.ip || null;
      const data = values;

      data.service = 'TBR';
      data.user_id = adminId || userId;
      data.customer_number = customerNumber;
      data.ip_address = ipAddress.slice(0, 15);

      axios.post(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/activity-log`, data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCloseSurvey = () => setShowSurvery(false);

  const handleSave = () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    } else {
      setFormErrors({});
    }

    if (enrolled) {
      setShowConfirm(true);
      return;
    }
    saveChanges();
  }

  const handleConfirm = () => {
    if (!subscriptionItem?.hold && formData.hold) {
      setShowSurvery(true);
      handleCloseConfirm();
      return;
    }
    saveChanges();
  }

  const onClose = (success: boolean, unEnroll?: boolean) => {
    setFormData(initialFormDataState)
    handleClose(success, unEnroll)
    setLoading(false)
    handleCloseConfirm()
    handleCloseSurvey()
  }

  const saveChanges = async (unEnroll = false) => {
    try {
      if(!formData.startDate) return
      const formattedStartDate = dayjs(formData.startDate)
        .clone()
        .set('date', 2)
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss[Z]');

      const isStartDateChanged =
        dayjs(formData.startDate).clone().startOf('day').isSame(dayjs(subscriptionItem?.startDate).clone().startOf('day')) === false;

      const userName = currentUser?.firstname
        ? `${currentUser.firstname} ${currentUser.lastname}`
        : currentUser?.email;

      const payload = {
        ...formData,
        itemId: subscriptionItem?.item_number,
        customerNumber,
        userName,
        hold: !enrolled ? false : formData.hold,
        startDate: isStartDateChanged ? new Date(formattedStartDate) : subscriptionItem?.startDate
      };

      setLoading(true);

      if (enrolled) {
        await axios.put(`${constants.SUBSCRIPTION_SERVICE_URL}/api/tbr/customer`, payload);
        const originalItem = {
          itemId: subscriptionItem?.itemId,
          quantity: subscriptionItem?.quantity,
          frequency: subscriptionItem?.frequency,
          startDate: subscriptionItem?.startDate,
          customerNumber,
          hold: subscriptionItem?.hold,
        }

        const logData = {
          old_value: JSON.stringify(originalItem),
          new_value: JSON.stringify(payload),
        }
        logActivity(logData);
        onClose(true);
      } else {
        await axios.post(`${constants.SUBSCRIPTION_SERVICE_URL}/api/tbr/customer`, payload);
        const logData = {
          old_value: null,
          new_value: JSON.stringify(payload),
        }
        logActivity(logData);
        onClose(true, unEnroll);
      }      
    }
    catch (err) {
      console.error(err);
      dispatch(addError('There was an error saving the subscription, please try again later'));
      setLoading(false);
    }
  }

  useMemo(() => {
    if (subscriptionItem) {
      const subscriptionItemDate = dayjs(subscriptionItem?.startDate).clone()
      setFormData({
        quantity: subscriptionItem?.quantity?.toString(),
        frequency: subscriptionItem?.frequency?.toString(),
        startDate: subscriptionItemDate && enrolled ? subscriptionItemDate : null,
        hold: subscriptionItem.hold
      })
    }
  }, [subscriptionItem])

  const handleCloseConfirm = () => {
    setShowConfirm(false);
  }

  const getImage = (itemNumber: number | undefined) => {
    if (itemNumber) {
      const itemImage = itemImages[`${itemNumber}.png`] ?? itemImages[`${itemNumber}.jpg`];
      return itemImage
    }
    return null;
  }

  return (
    <>
      <Dialog open={open && !showConfirm && !showSurvery} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
        <CloseModalButton handleClose={() => onClose(false)} />
        <StyledDialogTitle>Edit Your Subscription</StyledDialogTitle >
        <StyledDialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <img src={getImage(subscriptionItem?.item_number) ?? noImage} alt="item" style={{ width: '160px', display: 'block' }} className="m-r-12" />
          </Box>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <StyledTypography>{subscriptionItem?.name} #{subscriptionItem?.item_number}</StyledTypography>
            <FormControl size="small">
              <InputLabel id="quantity">Quantity</InputLabel>
              <Select
                size="small"
                labelId="quantity"
                label="Quantity"
                value={formData.quantity}
                onChange={e => handleEditChange("quantity", e.target.value)}
                fullWidth
              >
                {
                  quantities.map(quantity => (
                    <MenuItem key={quantity} value={quantity}>{quantity}</MenuItem>
                  ))
                }
              </Select>
              {formErrors['quantity'] && <FormHelperText error>Quantity is required</FormHelperText>}
            </FormControl>

            <FormControl  size="small">
              <InputLabel id="frequency">Frequency</InputLabel>
              <Select
                size="small"
                labelId="frequency"
                label="Frequency"
                value={formData.frequency}
                onChange={e => handleEditChange("frequency", e.target.value)}
                fullWidth
              >
                {
                  months.map(month => (
                    <MenuItem key={month} value={month}>{month} Months</MenuItem>
                  ))
                }
              </Select>
              {formErrors['frequency'] && <FormHelperText error>Frequency is required</FormHelperText>}
            </FormControl>

            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="en"
            >
              {/* minDate and disablePast causes weird behaviour and validation errors when original startDate is before the mindate */}
              {formData.startDate && formData.startDate < minStartDate && !datePickerIsOpen ? (
              <DatePicker
                label='Start Date'
                format="MM-DD-YYYY"
                views={['month', 'year']}
                openTo="month"
                value={formData.startDate}
                open={false}
                onOpen={handleOpenDatePicker}
                slotProps={{
                field: {
                  readOnly: true,
                },
                textField: { size: 'small' }
                }}
              />
              ) : (
              <DatePicker
                label='Start Date'
                onChange={(newDate) => {
                if (newDate) {
                  const firstOfMonth = newDate.set('date', 1).startOf('day');
                  handleEditChange('startDate', firstOfMonth);
                }
                }}
                onMonthChange={(newDate) => {
                if (newDate) {
                  const firstOfMonth = newDate.set('date', 1).startOf('day');
                  handleEditChange('startDate', firstOfMonth);
                }
                }}
                format="MM-DD-YYYY"
                views={['month', 'year']}
                openTo="month"
                disablePast
                minDate={minStartDate}
                value={formData.startDate}
                open={datePickerIsOpen}
                onClose={() => setDatePickerIsOpen(false)}
                onOpen={handleOpenDatePicker}
                slotProps={{
                field: {
                  readOnly: true
                },
                textField: { size: 'small' }
                }}
              />
              )}
            </LocalizationProvider>
          </Stack>
        </StyledDialogContent>
        <StyledDialogFooter>
          <FormGroup sx={{ padding: '7px' }}>
            {enrolled && (<FormControlLabel
              control={<Checkbox checked={formData.hold} onChange={e => handleEditChange('hold', e.target.checked)} />}
              label={<StyledTypography>Unenroll</StyledTypography>} />
            )}
          </FormGroup>
          <Button variant='outlined' onClick={() => onClose(false)}>Cancel</Button>
          <Button disabled={loading} variant="contained" onClick={handleSave}>
            {!loading ? ('Save') : (<CircularProgress size="30px" />)}
          </Button>
        </StyledDialogFooter>
      </Dialog >
      <Dialog open={showConfirm} onClose={handleCloseConfirm}>
        <CloseModalButton handleClose={handleCloseConfirm} />
        <StyledDialogTitle>Confirm Changes</StyledDialogTitle>
        <StyledDialogContent>
          <Stack alignItems='center'>
            <StyledTypography>You are changing an existing auto-renewal. Please confirm you want to make this change.
            </StyledTypography>
          </Stack>
        </StyledDialogContent>
        <StyledDialogFooter>
          <Button variant='outlined' onClick={handleCloseConfirm}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={loading}>
            {!loading ? ('Submit') : (<CircularProgress size="30px" />)}
          </Button>
        </StyledDialogFooter>
      </Dialog>
      <SurveyModal
        open={showSurvery}
        handleClose={handleCloseSurvey}
        save={saveChanges}
        currentUser={currentUser}
      />
    </>
  )
}

export default SubscriptionModal;